import React, { useEffect, useState } from "react";
import {
  Button, Card, Col, DatePicker, Form, InputNumber,
  Input, Popconfirm, Row, Space, Table, Typography, message,
} from "antd";
import { ArrowLeftOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const API_URL = process.env.REACT_APP_API_URL;

export default function FrmAnimalPesos() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [pesos, setPesos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [mostrarForm, setMostrarForm] = useState(false);

  const cargarPesos = async () => {
    try {
      setCargando(true);
      const resp = await axios.get(`${API_URL}/AnimalPeso/animal/${id}`);
      const lista = Array.isArray(resp.data) ? resp.data
        : Array.isArray(resp.data?.$values) ? resp.data.$values : [];
      setPesos(lista);
    } catch {
      message.error("Error cargando historial de pesos.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarPesos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onGuardar = async (values) => {
    try {
      setGuardando(true);
      await axios.post(`${API_URL}/AnimalPeso`, {
        animalId: Number(id),
        fecha: values.fecha.toISOString(),
        peso: values.peso,
        observacion: values.observacion || null,
      });
      message.success("Peso registrado.");
      form.resetFields();
      setMostrarForm(false);
      cargarPesos();
    } catch {
      message.error("Error guardando el peso.");
    } finally {
      setGuardando(false);
    }
  };

  const onEliminar = async (pesoId) => {
    try {
      await axios.delete(`${API_URL}/AnimalPeso/${pesoId}`);
      message.success("Registro eliminado.");
      cargarPesos();
    } catch {
      message.error("Error eliminando el registro.");
    }
  };

  const columns = [
    {
      title: "Fecha",
      dataIndex: "fecha",
      key: "fecha",
      render: (v) => dayjs(v).format("YYYY-MM-DD"),
      sorter: (a, b) => dayjs(a.fecha).valueOf() - dayjs(b.fecha).valueOf(),
      defaultSortOrder: "descend",
    },
    {
      title: "Peso (kg)",
      dataIndex: "peso",
      key: "peso",
      align: "right",
      render: (v) => `${Number(v).toFixed(1)} kg`,
    },
    {
      title: "Observación",
      dataIndex: "observacion",
      key: "observacion",
      ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "",
      key: "acciones",
      width: 80,
      render: (_, record) => (
        <Popconfirm
          title="¿Eliminar este registro?"
          okText="Sí"
          cancelText="No"
          onConfirm={() => onEliminar(record.id)}
        >
          <Button size="small" danger>Eliminar</Button>
        </Popconfirm>
      ),
    },
  ];

  const pesoMinimo = pesos.length ? Math.min(...pesos.map(p => p.peso)) : 0;
  const pesoMaximo = pesos.length ? Math.max(...pesos.map(p => p.peso)) : 0;
  const pesoActual = pesos.length ? pesos[0].peso : null;

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Volver
        </Button>
        <Title level={3} style={{ margin: 0 }}>Historial de Pesos</Title>
      </Space>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={8}>
          <Card size="small">
            <Text type="secondary">Peso actual</Text>
            <div style={{ fontSize: 22, fontWeight: 600 }}>
              {pesoActual != null ? `${Number(pesoActual).toFixed(1)} kg` : "—"}
            </div>
          </Card>
        </Col>
        <Col xs={8}>
          <Card size="small">
            <Text type="secondary">Mínimo</Text>
            <div style={{ fontSize: 22, fontWeight: 600 }}>
              {pesos.length ? `${Number(pesoMinimo).toFixed(1)} kg` : "—"}
            </div>
          </Card>
        </Col>
        <Col xs={8}>
          <Card size="small">
            <Text type="secondary">Máximo</Text>
            <div style={{ fontSize: 22, fontWeight: 600 }}>
              {pesos.length ? `${Number(pesoMaximo).toFixed(1)} kg` : "—"}
            </div>
          </Card>
        </Col>
      </Row>

      {mostrarForm ? (
        <Card title="Nuevo registro de peso" style={{ marginBottom: 16 }}>
          <Form form={form} layout="vertical" onFinish={onGuardar}>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Fecha"
                  name="fecha"
                  rules={[{ required: true, message: "La fecha es obligatoria." }]}
                  initialValue={dayjs()}
                >
                  <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Peso (kg)"
                  name="peso"
                  rules={[{ required: true, message: "El peso es obligatorio." }]}
                >
                  <InputNumber min={0} step={0.5} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Observación" name="observacion">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Space>
              <Button type="primary" htmlType="submit" loading={guardando}>
                Guardar
              </Button>
              <Button onClick={() => { setMostrarForm(false); form.resetFields(); }}>
                Cancelar
              </Button>
            </Space>
          </Form>
        </Card>
      ) : (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ marginBottom: 16 }}
          onClick={() => setMostrarForm(true)}
        >
          Registrar peso
        </Button>
      )}

      <Table
        rowKey="id"
        columns={columns}
        dataSource={pesos}
        loading={cargando}
        scroll={{ x: "max-content" }}
        pagination={{ pageSize: 20, showSizeChanger: false }}
      />
    </div>
  );
}
